// useFSLQuery.ts

import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseMutationOptions } from 'react-query';
import { isAfter } from 'date-fns';

export type QuerykeyString =
  | 'messages'
  | 'message'
  | 'thread'
  | 'sender'
  | 'numberOfReadMessages'
  | 'membership'
  | 'sentMessages'
  | 'districts'
  | 'school'
  | 'validateEmail'
  | 'employment'
  | 'positionsOfTrust'
  | 'schoolAssociations'
  | 'tileInformation'
  | 'courses'
  | 'cases';

/**
 * Wrap React Query's useQuery with default options (no refetchOnWindowFocus, no retry),
 * while allowing extra options via a `queryConfig`.
 */
export const useFSLQuery = <T extends unknown>(
  key: QuerykeyString | any[],
  fetchFunction: () => Promise<T>,
  queryConfig?: Omit<UseQueryOptions<T>, 'refetchOnWindowFocus' | 'retry'>
) => {
  const { data, error, status, isLoading, refetch } = useQuery<T>(key, fetchFunction, {
    refetchOnWindowFocus: false,
    retry: 0,
    ...(queryConfig || {}),
  });

  return {
    data: data as T,
    error,
    status,
    isLoading,
    refetch,
  };
};

/**
 * Wrap React Query's useMutation to handle optimistic updates, error rollbacks, etc.
 */
export const useFSLMutation = <TData extends unknown, TCache extends { lastUpdated?: string }>(
  key: QuerykeyString | any[],
  mutateFn: (data: TData) => Promise<TData>,
  updateCache: (newData: TData, previousValue: TCache | undefined) => TCache,
  mutationConfig?: Omit<UseMutationOptions<TData, unknown, TData, TCache | undefined>, 'onMutate' | 'onSettled' | 'onError'>,
  onError?: () => void
) => {
  // 1) Grab the queryClient instance
  const queryClient = useQueryClient();

  // 2) Get the previous value from the cache
  const previousValue = queryClient.getQueryData<TCache>(key);

  const mutate = useMutation<TData, unknown, TData, TCache | undefined>(mutateFn, {
    // 3) Handle optimistic update
    onMutate: async () => {
      // Cancel ongoing or scheduled fetches for this key
      await queryClient.cancelQueries(key);
      // Return snapshot for potential rollback in onError
      return previousValue;
    },

    // 4) onSettled callback
    onSettled: async () => {
      const previousLastUpdated = previousValue?.lastUpdated;

      if (previousLastUpdated) {
        let i = 0;
        let hasBeenUpdated = false;
        const delay = () => new Promise((res) => setTimeout(res, 500));

        while (i < 10 && !hasBeenUpdated) {
          i++;
          const data = queryClient.getQueryData<TCache>(key);
          const lastUpdated = data?.lastUpdated;

          if (lastUpdated) {
            // If the new lastUpdated is after the previous lastUpdated => success
            if (isAfter(new Date(lastUpdated), new Date(previousLastUpdated))) {
              hasBeenUpdated = true;
            } else {
              // Force a re-fetch, then wait
              queryClient.invalidateQueries(key);
              await delay();
            }
          } else {
            // If no lastUpdated is defined, bail out by invalidating queries
            hasBeenUpdated = true;
            queryClient.invalidateQueries(key);
          }
        }
      } else {
        // If we never had a lastUpdated, simply invalidate
        queryClient.invalidateQueries(key);
      }
    },

    // 5) onError callback
    onError: (err, newData, snapshotValue) => {
      if (snapshotValue) {
        // Roll back to the snapshot
        queryClient.setQueryData<TCache>(key, snapshotValue);
      }
      onError?.();
    },

    // 6) Spread additional config from the caller
    ...mutationConfig,
  });

  return { mutate };
};
